import { useEffect, useState } from 'react';
import groupBy from 'lodash.groupby';
import { IconLoader, IconDown } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { useLazyGetChatDocumentFileQuery } from '../hoc/store/services';
import { setScrolled, setNewMessageCount } from '../hoc/store/slices/general';
import { ChatMessagesTypes } from './types';
import formatСhatDate from '../hoc/helpers/format-chat-date';
import ChatMessage from './chat-message';

import cn from './messages.module.css';

const ChatMessages = ({
  messages,
  scrollWrapperRef,
  messagesIsFetching,
  scrollRef,
  getNextMessages,
  pagination,
  prevMessageIsFetching,
  setCurrentScrollTop,
  messageId,
  setMessageIsEdit,
  setSelectedForEditMessage,
  selectedForEditMessage,
  editMessageMarkdown,
  markdownRef,
}: ChatMessagesTypes) => {
  const [scrollButtonVisible, setScrollButtonVisible] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  const { scrolled, newMessageCount, employee } = useAppSelector(
    (state) => state.messages.general,
  );

  const dispatch = useAppDispatch();

  const [getChatDocumentFile, { isLoading: downloadInProgress, isFetching }] =
    useLazyGetChatDocumentFileQuery();

  const getFile = async (documentId: string, documentName: string) => {
    setSelectedDocumentId(documentId);

    const response = await getChatDocumentFile({
      id: messageId as string,
      documentId,
    });

    if (response && response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentName;
      hiddenElement.click();

      setSelectedDocumentId('');
    }
  };

  const groupedMessages = groupBy(
    messages?.filter((item) => typeof item.content === 'string'),
    (match) => new Date(match.createdAt).toLocaleDateString(),
  );

  const onScroll = (scroll: number) => {
    if (scroll === 0 && pagination?.hasNextPage) {
      getNextMessages(pagination?.endCursor?.toString());
    }

    if (scrollRef && scrollRef.current) {
      const { scrollHeight, clientHeight } = scrollRef.current;

      if (scroll < scrollHeight - clientHeight) {
        dispatch(setScrolled(true));
      } else {
        dispatch(setScrolled(false));
      }

      if (scroll <= scrollHeight - 1000) {
        setScrollButtonVisible(true);
      } else {
        setScrollButtonVisible(false);

        dispatch(setNewMessageCount(0));
      }
    }
  };

  const onScrollDown = () => {
    dispatch(setNewMessageCount(0));

    if (scrollRef && scrollRef.current) {
      const { scrollHeight } = scrollRef.current;
      // eslint-disable-next-line no-param-reassign
      scrollRef.current.scrollTop = scrollHeight;
    }
  };

  useEffect(() => {
    if (scrollRef.current && scrollWrapperRef?.current) {
      setTimeout(() => {
        if (
          scrollRef &&
          scrollRef.current &&
          (prevMessageIsFetching === undefined ||
            prevMessageIsFetching === false) &&
          !scrolled
        ) {
          const { scrollHeight } = scrollRef.current;

          if (scrollHeight) {
            // eslint-disable-next-line no-param-reassign
            scrollRef.current.scrollTop = scrollHeight;

            setCurrentScrollTop(scrollHeight);
          }
        }
      }, 2);
    }
  }, [prevMessageIsFetching, scrolled]);

  return (
    <div
      className={cn.scroll}
      ref={scrollRef}
      onScroll={(e: any) => {
        onScroll(e.target.scrollTop);
      }}
    >
      {messagesIsFetching && (
        <div className={cn.messagesLoading}>
          <IconLoader />
        </div>
      )}
      {Object.keys(groupedMessages)?.map((group) => {
        const firstMessageDate = new Date(groupedMessages[group][0].createdAt);

        let dateText = formatСhatDate(groupedMessages[group][0].createdAt);

        if (firstMessageDate.getDate() === new Date().getDate()) {
          dateText = 'Today';
        }

        if (new Date().getDate() - 1 === firstMessageDate.getDate()) {
          dateText = 'Yesterday';
        }

        let firstMessageName;

        if (groupedMessages[group][0]?.employee) {
          firstMessageName = groupedMessages[group][0]?.employee?.profile
            ? `${groupedMessages[group][0]?.employee?.profile?.firstName}  ${groupedMessages[group][0]?.employee?.profile?.lastName}`
            : groupedMessages[group][0].employeeId;
        } else if (groupedMessages[group][0]?.user) {
          firstMessageName = groupedMessages[group][0]?.user?.profile
            ? `${groupedMessages[group][0]?.user?.profile?.firstName}  ${groupedMessages[group][0]?.user?.profile?.lastName}`
            : groupedMessages[group][0].userId;
        }

        return (
          <div key={group} className={cn.messagesGroup}>
            <div className={cn.messagesDate}>
              <span className={cn.date}>{dateText}</span>
            </div>
            <div
              className={`${cn.newMessageContentUser} ${
                groupedMessages[group][0].employeeId ||
                groupedMessages[group][0].userId === employee?.id
                  ? cn.messageBlockRight
                  : ''
              }`}
            >
              {firstMessageName}
            </div>
            {groupedMessages[group].map((item, index) => {
              const date = new Date(item.createdAt);

              const dateString = `${date
                .getHours()
                .toString()
                .padStart(2, '0')}:${date
                .getMinutes()
                .toString()
                .padStart(2, '0')}`;

              let messageName;

              if (item.employee) {
                if (
                  groupedMessages[group][index - 1] &&
                  groupedMessages[group][index - 1].employeeId !==
                    item.employeeId
                ) {
                  if (item.employee) {
                    messageName = item?.employee?.profile
                      ? `${item?.employee?.profile?.firstName}  ${item?.employee?.profile?.lastName}`
                      : item.employeeId;
                  } else {
                    messageName = '-';
                  }
                }
              } else if (item.user) {
                if (
                  groupedMessages[group][index - 1] &&
                  groupedMessages[group][index - 1].userId !== item.userId
                ) {
                  messageName = item?.user?.profile
                    ? `${item?.user?.profile?.firstName}  ${item?.user?.profile?.lastName}`
                    : item.userId;
                }
              }

              return (
                <ChatMessage
                  key={item.id}
                  messageName={messageName}
                  item={item}
                  setMessageIsEdit={setMessageIsEdit}
                  setSelectedForEditMessage={setSelectedForEditMessage}
                  selectedForEditMessage={selectedForEditMessage}
                  editMessageMarkdown={editMessageMarkdown}
                  markdownRef={markdownRef}
                  getFile={getFile}
                  downloadInProgress={downloadInProgress}
                  isFetching={isFetching}
                  selectedDocumentId={selectedDocumentId}
                  dateString={dateString}
                />
              );
            })}
          </div>
        );
      })}
      {scrollButtonVisible && (
        <button
          type="button"
          className={cn.scrollButton}
          onClick={onScrollDown}
        >
          {newMessageCount?.toString() !== '0' && (
            <div className={cn.newMessagesCount}>{newMessageCount}</div>
          )}
          <IconDown />
        </button>
      )}
    </div>
  );
};

export default ChatMessages;
